import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { Link, useHistory } from 'react-router-dom';

import { AuthEntity } from '_entities';
import { IsAzAccount } from 'modules';
import { useIsReadyAndOffline } from 'hooks';

import { flattenPages, getTopLevelSections, getQuickActions } from '../_utils';
import RailIcon from '../Rail/icons';
import * as Styled from './styles';

const {
  getAuthenticatedUser,
  getUserEmail,
  getProfilePermissions,
  getModularityPermissions,
  getDashboardLinks,
  getUserRoleType,
} = AuthEntity.selectors;

const normalize = (str) => (str || '').toString().toLowerCase().trim();

const matchesQuery = (page, query) => {
  if (!query) return true;
  const haystack = `${normalize(page.title)} ${normalize(page.sectionTitle)} ${normalize(page.path)}`;
  return query.split(/\s+/).every((token) => haystack.includes(token));
};

const CommandPalette = ({ onClose }) => {
  const history = useHistory();
  const isOffline = useIsReadyAndOffline();
  const isAuthenticated = useSelector(getAuthenticatedUser);
  const userEmail = useSelector(getUserEmail);
  const userPermissions = useSelector(getProfilePermissions);
  const modularityPermissions = useSelector(getModularityPermissions);
  const dashboardInfoSections = useSelector(getDashboardLinks);
  const userRoleType = useSelector(getUserRoleType);
  const isAzAccount = IsAzAccount();

  const [query, setQuery] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);
  const inputRef = useRef(null);
  const resultsRef = useRef(null);

  const authContext = useMemo(
    () => ({
      isAuthenticated,
      userEmail,
      isOffline,
      dashboardInfoSections,
      isAzAccount,
      userRoleType,
      userPermissions,
      modularityPermissions,
    }),
    [
      isAuthenticated,
      userEmail,
      isOffline,
      dashboardInfoSections,
      isAzAccount,
      userRoleType,
      userPermissions,
      modularityPermissions,
    ],
  );

  const allEntries = useMemo(() => {
    const pages = flattenPages(authContext);
    const actions = getQuickActions(userPermissions, modularityPermissions);
    return [...actions, ...pages];
  }, [authContext, userPermissions, modularityPermissions]);
  const topSections = useMemo(() => getTopLevelSections(authContext), [authContext]);

  const filtered = useMemo(() => {
    const q = normalize(query);
    return allEntries.filter((entry) => matchesQuery(entry, q));
  }, [allEntries, query]);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  useEffect(() => {
    setActiveIndex(0);
  }, [query]);

  const selectPage = useCallback(
    (page) => {
      if (page?.path) {
        history.push(page.path);
        onClose();
      }
    },
    [history, onClose],
  );

  // Results are real links, so only a plain left-click is handled here — cmd/ctrl/shift-click and
  // middle-click fall through to the browser so they open a new tab/window. Right-click never gets here;
  // it shows the native link menu because the row has an href.
  const handleResultClick = useCallback(
    (e, page) => {
      if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) {
        onClose();
        return;
      }
      e.preventDefault();
      selectPage(page);
    },
    [selectPage, onClose],
  );

  // Guarded on button 1: `auxclick` fires for every non-primary button, so an unguarded handler would
  // also catch right-click and tear the palette down before the context menu could be used.
  const handleResultAuxClick = useCallback(
    (e) => {
      if (e.button === 1) onClose();
    },
    [onClose],
  );

  const jumpToSection = useCallback(
    (index) => {
      const section = topSections[index];
      if (!section) return;
      if (section.path) {
        history.push(section.path);
      } else if (section.subRoutes?.[0]?.path) {
        history.push(section.subRoutes[0].path);
      }
      onClose();
    },
    [history, onClose, topSections],
  );

  useEffect(() => {
    const handleKey = (e) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
        return;
      }
      if ((e.metaKey || e.ctrlKey) && /^[1-9]$/.test(e.key)) {
        e.preventDefault();
        jumpToSection(parseInt(e.key, 10) - 1);
        return;
      }
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setActiveIndex((prev) => Math.min(prev + 1, Math.max(filtered.length - 1, 0)));
        return;
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        setActiveIndex((prev) => Math.max(prev - 1, 0));
        return;
      }
      if (e.key === 'Enter') {
        e.preventDefault();
        selectPage(filtered[activeIndex]);
      }
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [filtered, activeIndex, selectPage, jumpToSection, onClose]);

  // Keep the active row in view when navigating with the keyboard.
  useEffect(() => {
    const list = resultsRef.current;
    if (!list) return;
    const node = list.children[activeIndex];
    if (node && typeof node.scrollIntoView === 'function') {
      node.scrollIntoView({ block: 'nearest' });
    }
  }, [activeIndex]);

  const handleBackdropMouseDown = useCallback(
    (e) => {
      if (e.target === e.currentTarget) onClose();
    },
    [onClose],
  );

  return (
    <Styled.Backdrop onMouseDown={handleBackdropMouseDown} role="presentation">
      <Styled.Panel role="dialog" aria-modal="true" aria-label="Command palette">
        <Styled.SearchRow>
          <Styled.SearchIconWrap aria-hidden>
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <circle cx="11" cy="11" r="7" />
              <path d="m21 21-4.3-4.3" />
            </svg>
          </Styled.SearchIconWrap>
          <Styled.SearchInput
            ref={inputRef}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Jump to anything · retailer, inspection, user…"
            aria-label="Search pages"
          />
          <Styled.EscChip>esc</Styled.EscChip>
        </Styled.SearchRow>

        <Styled.Results ref={resultsRef} role="listbox" aria-label="Search results">
          {filtered.length === 0 ? (
            <Styled.EmptyState>{query ? `No matches for "${query}"` : 'No accessible pages'}</Styled.EmptyState>
          ) : (
            filtered.map((page, index) => (
              <Styled.Result
                key={page.id}
                {...(page.path ? { as: Link, to: page.path } : {})}
                role="option"
                aria-selected={index === activeIndex}
                $active={index === activeIndex}
                onMouseEnter={() => setActiveIndex(index)}
                onClick={(e) => handleResultClick(e, page)}
                onAuxClick={handleResultAuxClick}
              >
                <Styled.ResultIcon>
                  <RailIcon name={page.sectionTitle} />
                </Styled.ResultIcon>
                <Styled.ResultText>
                  <Styled.ResultLabel>{page.title}</Styled.ResultLabel>
                  <Styled.ResultSubtitle>{page.subtitle}</Styled.ResultSubtitle>
                </Styled.ResultText>
                <Styled.OpenPill>{page.type === 'action' ? '⏎ run' : '⏎ open'}</Styled.OpenPill>
              </Styled.Result>
            ))
          )}
        </Styled.Results>

        <Styled.FooterHints>
          <Styled.Hint>
            <Styled.Kbd>↑↓</Styled.Kbd> select
          </Styled.Hint>
          <Styled.Hint>
            <Styled.Kbd>↵</Styled.Kbd> open
          </Styled.Hint>
          <Styled.Hint>
            <Styled.Kbd>esc</Styled.Kbd> close
          </Styled.Hint>
          <Styled.Hint>
            <Styled.Kbd>⌘1-9</Styled.Kbd> sections
          </Styled.Hint>
        </Styled.FooterHints>
      </Styled.Panel>
    </Styled.Backdrop>
  );
};

CommandPalette.propTypes = {
  onClose: PropTypes.func.isRequired,
};

export default CommandPalette;
